import { useMemo, useState } from 'react';
import {
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableCell,
  Button,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Imposto, INotaFiscal } from '../../../../../../types/api/fiscal/detalhe-nfe-entrada';
import DialogFullScreen from '../../../../../../components/DialogFullScreen';
import { DialogAprovarNotaFiscal } from './DialogAprovarNotaFiscal';
import { DialogRecusarNotaFiscal } from './DialogRecusarNotaFiscal';
import { DialogImpostosNotaFiscal } from './DialogImpostosNotaFiscal';
import { formatarValorMonetario } from '../../../../../../helpers/masks';

export const DialogVerificacaoContabil = ({
  open,
  handleClose,
  fiscal,
}: ModalVerificacaoContabilProps) => {

  const [openModal, setOpenModal] = useState({
    open: false,
    type: '' as 'aprovar' | 'recusar' | 'impostos' | '',
  });

  const [impostosEditados, setImpostosEditados] = useState<Record<number, Imposto>>({});
  const [taxasItens, setTaxasItens] = useState<NfItemTaxa[]>([]);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  const handleCloseModal = () => {
    setOpenModal({ open: false, type: '' });
  };

  const handleOpenModal = (tipoModal: 'aprovar' | 'recusar' | 'impostos', itemId?: number) => {
    if (tipoModal === 'aprovar') {
      setOpenModal({ open: true, type: 'aprovar' });
    }
    if (tipoModal === 'recusar') {
      setOpenModal({ open: true, type: 'recusar' });
    }
    if (tipoModal === 'impostos' && itemId !== undefined) {
      setSelectedItem(itemId);
      setOpenModal({ open: true, type: 'impostos' });
    }
  }

  const handleSalvarImpostos = (index: number, novosImpostos: Imposto) => {
    const itemId = fiscal.itens[index].id;

    setImpostosEditados(prev => ({
      ...prev,
      [itemId]: novosImpostos
    }));

    setTaxasItens(prev => {
      const taxasFiltradas = prev.filter(taxa => taxa.nfe_item_id !== itemId);
      return [...taxasFiltradas, {
        nfe_item_id: itemId,
        imposto: novosImpostos,
      }];
    });
  };

  const calcularAliquotaTotal = (impostos: Imposto | null): number => {
    if (!impostos) return 0;

    let aliquotaTotal = 0;

    Object.entries(impostos).forEach(([_, imposto]) => {
      if (imposto && typeof imposto === "object" && "aliquota" in imposto) {
        const aliquota = Number((imposto as any).aliquota ?? 0);
        if (!isNaN(aliquota) && aliquota > 0) {
          aliquotaTotal += aliquota;
        }
      }
    });

    return aliquotaTotal;
  };

  const itensComAliquotas = useMemo(() => {
    return fiscal.itens.map((produto) => {
      const impostos = impostosEditados[produto.id] || produto.imposto;
      return {
        ...produto,
        imposto: impostos,
        aliquotaTotal: calcularAliquotaTotal(impostos),
      };
    });
  }, [fiscal, impostosEditados]);

  return (
    <>
      <DialogFullScreen open={open} handleClose={handleClose} title="Verificação contábil">
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ overflowY: 'auto', height: '100%', p: 3 }}>
            <Typography variant="body1" paragraph>
              Preencha os detalhes abaixo para criar uma cotação com o fornecedor.
            </Typography>

            <Box sx={{ my: 3 }}>
              <Box sx={{ height: '1px', backgroundColor: 'grey.300', width: '100%' }} />
            </Box>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Produtos
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell><b>Produto</b></TableCell>
                    <TableCell align="right"><b>Quantidade solicitada</b></TableCell>
                    <TableCell><b>NCM/SH</b></TableCell>
                    <TableCell align="right"><b>Valor unitário</b></TableCell>
                    <TableCell align="right"><b>Valor Total</b></TableCell>
                    <TableCell align="right"><b>Alíquotas dos Impostos</b></TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    itensComAliquotas.map((produto, index) => (
                      <TableRow key={produto.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{produto.codigo_produto}</Typography>
                          <Typography variant="body2">{produto.descricao_produto}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {produto.quantidade_comercial}
                        </TableCell>
                        <TableCell>
                          {produto.ncm}
                        </TableCell>
                        <TableCell align="right">
                          {`R$ ${produto.valor_unitario.toString().replace('.', ',')}`}
                        </TableCell>
                        <TableCell align="right">
                          {formatarValorMonetario(produto.valor_total_produto)}
                        </TableCell>
                        <TableCell align="right">
                          {produto.aliquotaTotal ? `${produto.aliquotaTotal.toFixed(2)}%` : '0%'}
                          {impostosEditados[produto.id] && (
                            <Typography variant="caption" display="block" color="primary">
                              (Editado)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            aria-label="ver mais detalhes"
                            onClick={() => handleOpenModal('impostos', index)}
                          >
                            <VisibilityIcon color='primary' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button variant="outlined" color="primary" onClick={() => handleOpenModal('recusar')}>
                Recusar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleOpenModal('aprovar')}
              >
                Aprovar
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </DialogFullScreen>
      {
        selectedItem !== null && openModal.open && openModal.type === 'impostos' &&
        <DialogImpostosNotaFiscal
          open={openModal.open}
          handleClose={handleCloseModal}
          impostos={impostosEditados[fiscal.itens[selectedItem].id] || fiscal.itens[selectedItem].imposto}
          onSave={(novosImpostos) => handleSalvarImpostos(selectedItem, novosImpostos)}
        />
      }
      {
        openModal.open && openModal.type === 'aprovar' &&
        <DialogAprovarNotaFiscal
          onSuccess={() => {
            handleClose();
          }}
          open={openModal.open}
          handleClose={handleCloseModal}
          fiscal={fiscal}
          taxasItens={taxasItens}
        />
      }
      {
        openModal.open && openModal.type === 'recusar' &&
        <DialogRecusarNotaFiscal
          open={openModal.open}
          handleClose={handleCloseModal}
          fiscal={fiscal}
          onSuccess={() => {
            handleClose();
          }}
        />
      }

    </>
  );
};

interface ModalVerificacaoContabilProps {
  open: boolean;
  handleClose: () => void;
  fiscal: INotaFiscal;
}

export interface NfItemTaxa {
  nfe_item_id: number;
  imposto: Imposto;
}